<template>
  <AuthContainer>
    <div class="text-left my-8">
      <h1 class="text-3xl md:text-4xl font-semibold mb-4">
        <template v-if="!hasSentEmail">{{ $t('auth.forgotPassword.title') }}</template>
        <template v-else>{{ $t('auth.forgotPasswordOk.title') }}</template>
      </h1>
      <p class="text-gray-700 text-lg">
        <template v-if="!hasSentEmail">{{ $t('auth.forgotPassword.subtitle') }}</template>
        <template v-else>{{ $t('auth.forgotPasswordOk.subtitle', { email: email }) }}</template>
      </p>
    </div>

    <form @submit.prevent="onSubmit" class="space-y-6 text-sm">
      <BaseInput
        v-if="!hasSentEmail"
        id="forgot-password"
        :label="$t('auth.forgotPassword.form.email')"
        v-model="email"
        width="w-full"
        :disabled="isLoading"
        :error="errorMessage"
        :onChange="handleChange"
        placeholder="Enter your email"
      />

      <div
        v-if="forgotPasswordError.length > 0"
        class="p-3 bg-red-50 border border-red-200 rounded-md"
      >
        <p class="text-sm text-red-600 text-center">
          {{ $t('auth.forgotPassword.error') }}
        </p>
      </div>
      <BaseButton
        type="submit"
        :loading="isLoading"
        :disabled="!!errorMessage || isLoading || countdown > 0"
        width="w-full"
      >
        {{
          !hasSentEmail
            ? $t('auth.forgotPassword.form.submit')
            : countdown > 0
              ? $t('auth.verifyEmail.resendIn', { seconds: countdown })
              : isLoading
                ? $t('auth.verifyEmail.resending')
                : $t('auth.verifyEmail.resend')
        }}
      </BaseButton>
    </form>

    <router-link
      append
      class="hover:text-main-hover cursor-pointer font-medium transition-colors mt-8 md:mt-12 2xl:mt-14 text-center text-md block"
      :to="{ name: RoutesKey.AUTH_SIGN_IN }"
    >
      {{ $t('auth.verifyEmail.returnToSignIn') }} ?
    </router-link>
  </AuthContainer>
</template>

<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import { UserValidator } from '@/services/userAndAuth/forms/UserValidator'
import { AuthenticationService } from '@/services/userAndAuth/AuthenticationService'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import { useI18n } from 'vue-i18n'

import { RoutesKey } from '@/models/enums/app/RoutesKey'
import { ref } from 'vue'
import AuthContainer from './AuthContainer.vue'
import { POSITION, useToast } from 'vue-toastification'

const { t } = useI18n()

const isLoading = ref(false)
const toast = useToast()
const forgotPasswordError = ref('')
const hasSentEmail = ref(false)

const countdown = ref(0)
let countdownInterval: number | null = null
const validationSchema = toTypedSchema(
  z.object({
    email: UserValidator.emailSchema,
  }),
)
const startCountdown = () => {
  countdown.value = 60
  countdownInterval = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0 && countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
  }, 1000)
}

const { handleSubmit } = useForm({
  validationSchema,
})

const {
  value: email,
  errorMessage,
  handleChange,
} = useField<string>('email', '', { validateOnValueUpdate: false })

const onSubmit = handleSubmit(async (values) => {
  try {
    isLoading.value = true
    await AuthenticationService.forgotPassword(values.email)
    toast.success(t('auth.forgotPassword.success'), {
      position: POSITION.TOP_RIGHT,
    })
    startCountdown()
    isLoading.value = false
    hasSentEmail.value = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    isLoading.value = false
    hasSentEmail.value = true
    forgotPasswordError.value = error.message
  }
})
</script>
